import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { playClickSound, playSlapSound, playBossSound, playSuccessSound } from '@/utils/sounds';
import { triggerButtonHaptic } from '@/utils/haptics';
import { GachiReward } from '@/components/GachiReward';

interface PowerUp {
  name: string;
  threshold: number;
  emoji: string;
  multiplier: number;
}

const powerUps: PowerUp[] = [
  { name: 'Warm Up', threshold: 10, emoji: '🔥', multiplier: 1 },
  { name: 'Full Power', threshold: 25, emoji: '💪', multiplier: 2 },
  { name: 'Beast Mode', threshold: 50, emoji: '🦁', multiplier: 3 },
  { name: 'Dungeon Master', threshold: 100, emoji: '👑', multiplier: 5 },
  { name: 'Boss of This Gym', threshold: 200, emoji: '♂️', multiplier: 10 },
];

const gachiQuotes = [
  'Take it boy!',
  'Oh yes sir!',
  'Thank you sir!',
  'Deep dark fantasies...',
  'I\'ll show you who\'s the boss!',
  '300 bucks!',
  'Come on college boy!',
  'Full master!',
];

export function GachiClicker() {
  const [power, setPower] = useState(0);
  const [clicks, setClicks] = useState(0);
  const [currentPowerUp, setCurrentPowerUp] = useState<PowerUp | null>(null);
  const [floatingNumbers, setFloatingNumbers] = useState<Array<{ id: number; value: number; x: number }>>([]);
  const [quote, setQuote] = useState('');
  const [showGachiReward, setShowGachiReward] = useState(false);
  const [hasWon, setHasWon] = useState(false);

  const getCurrentMultiplier = useCallback(() => {
    const activePowerUp = [...powerUps].reverse().find(p => power >= p.threshold);
    return activePowerUp?.multiplier || 1;
  }, [power]);

  const handleClick = useCallback(() => {
    const multiplier = getCurrentMultiplier();
    const gainedPower = multiplier;
    
    setClicks(prev => prev + 1);
    setPower(prev => {
      const newPower = prev + gainedPower;
      
      // Check for power-up unlocks
      const newPowerUp = powerUps.find(p => prev < p.threshold && newPower >= p.threshold);
      if (newPowerUp) {
        setCurrentPowerUp(newPowerUp);
        playBossSound();
        // Show random quote
        setQuote(gachiQuotes[Math.floor(Math.random() * gachiQuotes.length)]);
        setTimeout(() => setQuote(''), 2000);
      } else if (clicks % 10 === 0) {
        playSlapSound();
      } else {
        playClickSound();
      }

      // Win condition
      if (newPower >= 300 && !hasWon) {
        setHasWon(true);
        playSuccessSound();
        setTimeout(() => setShowGachiReward(true), 500);
      }

      return newPower;
    });

    triggerButtonHaptic();

    // Add floating number
    const id = Date.now();
    const x = Math.random() * 60 + 20; // 20-80% position
    setFloatingNumbers(prev => [...prev, { id, value: gainedPower, x }]);
    setTimeout(() => {
      setFloatingNumbers(prev => prev.filter(n => n.id !== id));
    }, 1000);
  }, [clicks, getCurrentMultiplier, hasWon]);

  const handleReset = useCallback(() => {
    setPower(0);
    setClicks(0);
    setCurrentPowerUp(null);
    setHasWon(false);
    setQuote('');
  }, []);

  const progressToNext = (() => {
    const nextPowerUp = powerUps.find(p => power < p.threshold);
    if (!nextPowerUp) return 100;
    const prevThreshold = [...powerUps].reverse().find(p => power >= p.threshold)?.threshold || 0;
    return ((power - prevThreshold) / (nextPowerUp.threshold - prevThreshold)) * 100;
  })();

  const nextPowerUp = powerUps.find(p => power < p.threshold);

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-white/90">
            Gachi Clicker ♂️
          </h3>
          <div className="text-right">
            <div className="text-2xl font-bold text-amber-400">{power}</div>
            <div className="text-xs text-white/50">Power Level</div>
          </div>
        </div>

        {/* Current Power-Up Badge */}
        <AnimatePresence mode="wait">
          {currentPowerUp && (
            <motion.div
              key={currentPowerUp.name}
              initial={{ opacity: 0, scale: 0.8, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 10 }}
              className="flex items-center justify-center gap-2 p-3 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-lg"
            >
              <span className="text-2xl">{currentPowerUp.emoji}</span>
              <span className="font-bold text-amber-400">{currentPowerUp.name}</span>
              <span className="text-sm text-white/60">x{currentPowerUp.multiplier}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Quote */}
        <AnimatePresence>
          {quote && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="text-center text-amber-300 italic font-medium"
            >
              "{quote}"
            </motion.p>
          )}
        </AnimatePresence>

        {/* Progress to Next Power-Up */}
        {nextPowerUp && (
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-white/60">
              <span>Next: {nextPowerUp.emoji} {nextPowerUp.name}</span>
              <span>{power}/{nextPowerUp.threshold}</span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-amber-500 to-orange-500"
                initial={{ width: 0 }}
                animate={{ width: `${progressToNext}%` }}
                transition={{ type: 'spring', stiffness: 100 }}
              />
            </div>
          </div>
        )}

        {/* Main Click Button */}
        <div className="relative">
          {/* Floating numbers */}
          <AnimatePresence>
            {floatingNumbers.map(({ id, value, x }) => (
              <motion.div
                key={id}
                initial={{ opacity: 1, y: 0 }}
                animate={{ opacity: 0, y: -60 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className="absolute text-amber-400 font-bold text-lg pointer-events-none"
                style={{ left: `${x}%`, top: '20%' }}
              >
                +{value}
              </motion.div>
            ))}
          </AnimatePresence>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleClick}
            disabled={hasWon}
            className={`w-full py-8 px-4 rounded-lg font-bold text-xl transition-all duration-300 min-h-[120px] relative overflow-hidden ${
              hasWon
                ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white cursor-default'
                : power >= 100
                  ? 'bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 text-white'
                  : power >= 50
                    ? 'bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-500 hover:to-amber-500 text-white'
                    : 'bg-white/10 hover:bg-white/20 text-white/90 border border-white/20'
            }`}
          >
            {hasWon ? (
              <>
                <span className="text-2xl">👑 BOSS OF THIS GYM 👑</span>
                <br />
                <span className="text-sm opacity-80">You did it!</span>
              </>
            ) : (
              <>
                <span className="text-3xl mb-2 block">💪</span>
                <span>CLICK TO TRAIN</span>
                <br />
                <span className="text-sm opacity-70">+{getCurrentMultiplier()} power per click</span>
              </>
            )}

            {/* Pulse effect when high power */}
            {power >= 100 && !hasWon && (
              <motion.div
                animate={{
                  scale: [1, 1.05, 1],
                  opacity: [0.5, 0.2, 0.5],
                }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="absolute inset-0 bg-white/20 rounded-lg"
              />
            )}
          </motion.button>
        </div>

        {/* Stats */}
        <div className="flex justify-between text-sm text-white/50">
          <span>Clicks: {clicks}</span>
          <span>Multiplier: x{getCurrentMultiplier()}</span>
        </div>

        {/* Power-Up Milestones */}
        <div className="flex flex-wrap gap-2 justify-center">
          {powerUps.map((pu) => (
            <motion.div
              key={pu.name}
              className={`px-3 py-1 rounded-full text-xs ${
                power >= pu.threshold
                  ? 'bg-amber-500/30 text-amber-300 border border-amber-500/50'
                  : 'bg-white/5 text-white/30 border border-white/10'
              }`}
              whileHover={{ scale: 1.05 }}
            >
              {pu.emoji} {pu.threshold}
            </motion.div>
          ))}
        </div>

        {/* Reset Button */}
        {(power > 0 || hasWon) && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={handleReset}
            className="w-full py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-sm text-sm text-white/50 transition-colors"
          >
            Reset Progress
          </motion.button>
        )}
      </div>

      {/* Gachi Reward */}
      <GachiReward
        visible={showGachiReward}
        onDismiss={() => setShowGachiReward(false)}
      />
    </>
  );
}

